import { useState, useEffect, useRef, useCallback } from 'react';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { MessageCircle, Send, Sparkles, Loader2, RefreshCw, ArrowLeft, Image as ImageIcon } from 'lucide-react';

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) + ' ' + d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
};

export default function WhatsAppInbox() {
  const [status, setStatus] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const syncFromTwilio = async () => {
    setSyncing(true);
    try {
      const r = await axiosInstance.post('/whatsapp/sync');
      const d = r.data || {};
      if (d.imported > 0) {
        toast.success(`${d.imported} bericht(en) opgehaald bij Twilio`);
      } else if ((d.twilio_incoming || 0) === 0) {
        toast.info('Geen inkomende berichten bij Twilio gevonden — controleer of je nummer door Meta is goedgekeurd en of klanten naar het juiste nummer appen');
      } else {
        toast.info('Alles is al up-to-date');
      }
      await loadConversations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ophalen bij Twilio mislukt');
    } finally {
      setSyncing(false);
    }
  };

  const loadConversations = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/whatsapp/conversations');
      setConversations(r.data.conversations || []);
    } catch {}
  }, []);

  const loadThread = useCallback(async (number) => {
    try {
      const r = await axiosInstance.get(`/whatsapp/messages?number=${encodeURIComponent(number)}`);
      setMessages(r.data.messages || []);
      const lastIn = [...(r.data.messages || [])].reverse().find((m) => m.direction === 'in');
      if (lastIn?.ai_draft && !reply) setReply(lastIn.ai_draft);
    } catch {}
  }, [reply]);

  useEffect(() => {
    (async () => {
      try {
        const s = await axiosInstance.get('/whatsapp/status');
        setStatus(s.data);
        await loadConversations();
      } finally {
        setLoading(false);
      }
    })();
  }, [loadConversations]);

  useEffect(() => {
    if (!active) return;
    setReply('');
    loadThread(active);
    const iv = setInterval(() => { loadThread(active); loadConversations(); }, 15000);
    return () => clearInterval(iv);
  }, [active]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const generateDraft = async () => {
    setDrafting(true);
    try {
      const r = await axiosInstance.post('/whatsapp/draft', { number: active });
      setReply(r.data.draft || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Concept genereren mislukt');
    } finally {
      setDrafting(false);
    }
  };

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axiosInstance.post('/whatsapp/send', { to_number: active, body: reply.trim() });
      setReply('');
      toast.success('Verzonden via WhatsApp');
      await loadThread(active);
      await loadConversations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versturen mislukt');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-600"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Laden...</div>;

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]" data-testid="whatsapp-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-[#25D366]" /> WhatsApp
          </h1>
          <p className="text-gray-500 text-sm">
            Klantberichten met AI-conceptantwoorden{status?.from_number ? ` · afzender ${status.from_number}` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={syncFromTwilio} disabled={syncing} className="border-[#25D366] text-[#128C7E]" data-testid="whatsapp-twilio-sync-btn">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />} Ophalen bij Twilio
          </Button>
          <Button variant="outline" size="sm" onClick={loadConversations} data-testid="whatsapp-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Vernieuwen
          </Button>
        </div>
      </div>

      {status?.configured && !status?.last_webhook_at && (
        <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800" data-testid="whatsapp-webhook-warning">
          <p className="font-semibold mb-1">⚠️ Er komen nog geen klantberichten binnen bij Twilio</p>
          {status?.account_type === 'Trial' ? (
            <div className="space-y-1">
              <p>Je Twilio-account is nog een <b>Trial-account</b>. Daarmee kan je eigen nummer ({status.from_number}) nog géén WhatsApp-berichten van klanten ontvangen — berichten komen dan gewoon op je telefoon binnen, niet in dit systeem.</p>
              <p className="font-semibold">Zo activeer je het:</p>
              <ol className="list-decimal ml-4 space-y-0.5">
                <li>Upgrade je Twilio-account (Console → Billing → Upgrade)</li>
                <li>Registreer {status.from_number} als WhatsApp Sender (Messaging → Senders → WhatsApp senders) en doorloop de Meta-goedkeuring</li>
                <li>Stel bij die sender de webhook in ("When a message comes in"):</li>
              </ol>
            </div>
          ) : (
            <p>Controleer in de Twilio Console (WhatsApp sender {status.from_number}) of bij "When a message comes in" deze webhook-URL staat:</p>
          )}
          <code className="block mt-1 bg-white border border-amber-200 rounded px-2 py-1 break-all select-all">{process.env.REACT_APP_BACKEND_URL}/api/whatsapp/webhook</code>
          <p className="mt-1">Met "Ophalen bij Twilio" kun je daarna berichten ook handmatig binnenhalen.</p>
        </div>
      )}

      <div className="flex-1 flex gap-4 min-h-0">
        <Card className={`bg-white border-gray-200 overflow-y-auto ${active ? 'hidden md:block md:w-80' : 'w-full md:w-80'}`} data-testid="whatsapp-conversations">
          {conversations.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">
              <MessageCircle className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              Nog geen berichten.<br />Zodra een klant appt verschijnt het gesprek hier.
            </div>
          ) : (
            conversations.map((c) => (
              <button key={c.number} onClick={() => setActive(c.number)}
                className={`w-full text-left p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${active === c.number ? 'bg-[#25D366]/5' : ''}`}
                data-testid={`whatsapp-conversation-${c.number.replace('+', '')}`}>
                <div className="flex justify-between items-center">
                  <p className="font-semibold text-sm text-gray-800 truncate">{c.profile_name || c.number}</p>
                  {c.unanswered > 0 && (
                    <span className="bg-[#25D366] text-white text-[10px] font-bold rounded-full px-2 py-0.5">{c.unanswered}</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 truncate">{c.last_direction === 'out' ? 'Jij: ' : ''}{c.last_body || '[media]'}</p>
                <p className="text-[10px] text-gray-400">{fmtTime(c.last_at)}</p>
              </button>
            ))
          )}
        </Card>

        {active ? (
          <Card className="flex-1 bg-white border-gray-200 flex flex-col min-h-0">
            <div className="border-b border-gray-100 p-3 flex items-center gap-2">
              <Button variant="ghost" size="sm" className="md:hidden" onClick={() => setActive(null)}><ArrowLeft className="w-4 h-4" /></Button>
              <div>
                <p className="font-semibold text-sm text-gray-800">{conversations.find((c) => c.number === active)?.profile_name || active}</p>
                <p className="text-xs text-gray-400">{active}</p>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#e5ded8]/30" data-testid="whatsapp-thread">
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.direction === 'out' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm shadow-sm ${m.direction === 'out' ? 'bg-[#d9fdd3]' : 'bg-white'}`}>
                    {(m.media || []).map((url, i) => (
                      <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/media?url=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-600 text-xs mb-1">
                        <ImageIcon className="w-3 h-3" /> Bijlage {i + 1}
                      </a>
                    ))}
                    <p className="whitespace-pre-wrap text-gray-800">{m.body}</p>
                    <p className="text-[10px] text-gray-400 text-right mt-0.5">{fmtTime(m.created_at)}</p>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            <div className="border-t border-gray-100 p-3 space-y-2">
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={generateDraft} disabled={drafting} className="border-[#25D366] text-[#128C7E]" data-testid="whatsapp-ai-draft-btn">
                  {drafting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />} AI-concept
                </Button>
              </div>
              <div className="flex gap-2">
                <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={2}
                  placeholder="Typ je antwoord of klik op AI-concept..."
                  className="flex-1 resize-none rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#25D366]/40"
                  data-testid="whatsapp-reply-input" />
                <Button onClick={send} disabled={sending || !reply.trim()} className="bg-[#25D366] hover:bg-[#1eb356]" data-testid="whatsapp-send-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <Card className="flex-1 bg-white border-gray-200 hidden md:flex items-center justify-center text-gray-400 text-sm">
            Kies links een gesprek
          </Card>
        )}
      </div>
    </div>
  );
}
